import type { SupportedLocale } from '@/types';
import { SUPPORTED_LOCALES } from '@/types';

/**
 * Resolves a language value to a supported locale.
 *
 * 1. Returns 'en' if the input is missing or not a string
 * 2. Normalizes input to lowercase
 * 3. Extracts the primary language tag (portion before `-` or `_`)
 * 4. Returns the primary tag if it is 'en' or 'ja'
 * 5. Falls back to 'en' for all other values
 */
export function resolveLocale(lang?: unknown): SupportedLocale {
  if (typeof lang !== 'string' || lang.length === 0) {
    return 'en';
  }

  const normalized = lang.toLowerCase();
  const primaryTag = normalized.split(/[-_]/)[0];

  if ((SUPPORTED_LOCALES as readonly string[]).includes(primaryTag)) {
    return primaryTag as SupportedLocale;
  }

  return 'en';
}

/**
 * Maps a resolved locale to the full Accept-Language header value
 * expected by the BFF API.
 *
 * - 'en' → 'en-US'
 * - 'ja' → 'ja-JP'
 */
export function mapLocaleToAcceptLanguage(locale: SupportedLocale): string {
  return locale === 'ja' ? 'ja-JP' : 'en-US';
}

/**
 * Maps a resolved locale to the language tag used for persistence and for
 * outbound navigation URLs. Matches the value format MyVaisala stores and
 * validates against (`'en' | 'ja-JP'`).
 */
export function mapLocaleToLanguageTag(locale: SupportedLocale): string {
  return locale === 'ja' ? 'ja-JP' : 'en';
}

/**
 * Query parameter carrying the locale to another origin.
 *
 * `localStorage` is origin-scoped, so cross-domain navigation from the menu
 * cannot share the language preference through storage. The locale travels as
 * a query parameter instead, which the destination application reads on load.
 */
export const LOCALE_QUERY_PARAM = 'lang';

/**
 * Returns `url` with the locale appended as a query parameter.
 *
 * Absolute URLs only — relative or malformed values are returned untouched
 * rather than throwing, since menu URLs come from remote configuration.
 * An existing `lang` parameter is overwritten so the current locale wins.
 */
export function withLocaleParam(url: string, locale?: SupportedLocale): string {
  if (!locale) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set(LOCALE_QUERY_PARAM, mapLocaleToLanguageTag(locale));
    return parsed.toString();
  } catch {
    return url;
  }
}
